import { apiFetch } from './client';

// TeamCategory mirrors the API shape served by
// /api/v1/organizations/{slug}/team-categories.
//
// Phase 1 of the team-categories-and-scheduling-rules plan: categories are a
// flat per-org taxonomy used to bucket teams. Phase 2 attaches scheduling
// rules to each category for AI template generation.
export interface TeamCategory {
  id: string;
  name: string;
  description: string;
  sort_order: number;
  // Number of active teams currently assigned to this category. Populated by
  // the list endpoint; single-category fetches (GET .../{id}) leave it 0.
  team_count: number;
  // How many days before an activity a calling auto-sends for teams in this
  // category: -1 = off, 0 = on create, N = N days before. The activity form
  // inherits this default (and can override it per activity).
  calling_lead_days: number;
  created_at: string;
}

export interface ListTeamCategoriesResponse {
  categories: TeamCategory[];
}

export async function listTeamCategories(
  token: string | null,
  slug: string,
): Promise<ListTeamCategoriesResponse> {
  return apiFetch<ListTeamCategoriesResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories`,
    { token },
  );
}

export interface CreateTeamCategoryInput {
  name: string;
  description?: string;
  // Omit for the platform default (7 days before); -1 = off; 0 = on create;
  // N = N days before.
  calling_lead_days?: number;
}

export async function createTeamCategory(
  token: string | null,
  slug: string,
  input: CreateTeamCategoryInput,
): Promise<TeamCategory> {
  return apiFetch<TeamCategory>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories`,
    { method: 'POST', body: input, token },
  );
}

export interface UpdateTeamCategoryInput {
  name: string;
  description: string;
  // Omit to leave unchanged; -1 = off; 0 = on create; N = N days before.
  calling_lead_days?: number;
}

export async function updateTeamCategory(
  token: string | null,
  slug: string,
  id: string,
  input: UpdateTeamCategoryInput,
): Promise<TeamCategory> {
  return apiFetch<TeamCategory>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: input, token },
  );
}

// deleteTeamCategory soft-deletes the category. force=true tells the server
// to clear category_id on every still-linked team before deleting (atomic
// with the delete itself); leave force unset / false to get the default
// "refuse with category_in_use when teams are still attached" behavior the
// cascade-confirm UI relies on for its preflight count.
export async function deleteTeamCategory(
  token: string | null,
  slug: string,
  id: string,
  options: { force?: boolean } = {},
): Promise<void> {
  const qs = options.force ? '?force=true' : '';
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/${encodeURIComponent(id)}${qs}`,
    { method: 'DELETE', token },
  );
}

// Reorder rewrites sort_order for every category in `ids` so they take
// positions 0..N-1 in that exact order. Send the full list so the SPA's
// in-memory order matches disk after the call.
export async function reorderTeamCategories(
  token: string | null,
  slug: string,
  ids: string[],
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/reorder`,
    { method: 'POST', body: { ids }, token },
  );
}

// setTeamCategoryMembers replaces the full set of teams in a category in one
// atomic call. Teams in teamIDs that aren't already in this category are
// moved in (regardless of which other category they were in); teams currently
// in the category but absent from teamIDs are cleared (made uncategorised).
export async function setTeamCategoryMembers(
  token: string | null,
  slug: string,
  categoryID: string,
  teamIDs: string[],
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/${encodeURIComponent(categoryID)}/teams`,
    { method: 'PUT', body: { team_ids: teamIDs }, token },
  );
}

// ─── Scheduling rules ───────────────────────────────────────────────────
//
// The Rules type mirrors the Go shape in internal/services/teamcategory.
// Every value field is `T | null` where null means "unset" (and on the
// override layer, "inherit from category"). Strictness is "" when unset.

export type RuleStrictness = '' | 'must' | 'target' | 'prefer';

export interface Rules {
  sessions_per_week_target: number | null;
  sessions_per_week_min: number | null;
  sessions_per_week_max: number | null;
  sessions_per_week_strictness: RuleStrictness;

  session_duration_minutes_target: number | null;
  session_duration_minutes_min: number | null;
  session_duration_minutes_max: number | null;
  session_duration_strictness: RuleStrictness;

  time_window_start: string | null; // HH:MM
  time_window_end: string | null;   // HH:MM
  time_window_strictness: RuleStrictness;

  allowed_weekdays: [boolean, boolean, boolean, boolean, boolean, boolean, boolean] | null;
  allowed_weekdays_strictness: RuleStrictness;

  allowed_locations_strictness: RuleStrictness;

  // Location-size fraction. Stored as the denominator of 1/N: 1 = whole,
  // 2 = half, 4 = quarter, 8 = eighth. preferred = aim for; min = will
  // accept down to (larger denominator = smaller share). Both null = "no
  // preference".
  preferred_location_fraction_denominator: number | null;
  min_location_fraction_denominator: number | null;
  location_fraction_strictness: RuleStrictness;

  min_leaders_present: number | null;
  min_leaders_present_strictness: RuleStrictness;
  head_coach_required: boolean | null;
  head_coach_required_strictness: RuleStrictness;

  updated_at?: string;
}

// emptyRules — every field null/'unset'. Used as the initial state when no
// rule row exists yet on the backend.
export function emptyRules(): Rules {
  return {
    sessions_per_week_target: null,
    sessions_per_week_min: null,
    sessions_per_week_max: null,
    sessions_per_week_strictness: '',
    session_duration_minutes_target: null,
    session_duration_minutes_min: null,
    session_duration_minutes_max: null,
    session_duration_strictness: '',
    time_window_start: null,
    time_window_end: null,
    time_window_strictness: '',
    allowed_weekdays: null,
    allowed_weekdays_strictness: '',
    allowed_locations_strictness: '',
    preferred_location_fraction_denominator: null,
    min_location_fraction_denominator: null,
    location_fraction_strictness: '',
    min_leaders_present: null,
    min_leaders_present_strictness: '',
    head_coach_required: null,
    head_coach_required_strictness: '',
  };
}

export async function getCategoryRules(
  token: string | null,
  slug: string,
  categoryID: string,
): Promise<Rules> {
  return apiFetch<Rules>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/${encodeURIComponent(categoryID)}/rules`,
    { token },
  );
}

export async function setCategoryRules(
  token: string | null,
  slug: string,
  categoryID: string,
  rules: Rules,
): Promise<Rules> {
  return apiFetch<Rules>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/${encodeURIComponent(categoryID)}/rules`,
    { method: 'PUT', body: rules, token },
  );
}

// ─── Allowed locations ──────────────────────────────────────────────────

export interface AllowedLocationEntry {
  location_id: string;
  location_name?: string;
  zone_id: string | null;
  zone_name?: string | null;
  preference_order: number;
}

export interface AllowedLocationsResponse {
  entries: AllowedLocationEntry[];
}

export async function getCategoryAllowedLocations(
  token: string | null,
  slug: string,
  categoryID: string,
): Promise<AllowedLocationsResponse> {
  return apiFetch<AllowedLocationsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/${encodeURIComponent(categoryID)}/allowed-locations`,
    { token },
  );
}

export async function setCategoryAllowedLocations(
  token: string | null,
  slug: string,
  categoryID: string,
  entries: AllowedLocationEntry[],
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/team-categories/${encodeURIComponent(categoryID)}/allowed-locations`,
    { method: 'PUT', body: { entries }, token },
  );
}

// ─── Team-level override + effective resolver ───────────────────────────

export async function getTeamRuleOverride(
  token: string | null,
  slug: string,
  teamID: string,
): Promise<Rules> {
  return apiFetch<Rules>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/scheduling-rule-override`,
    { token },
  );
}

export async function setTeamRuleOverride(
  token: string | null,
  slug: string,
  teamID: string,
  rules: Rules,
): Promise<Rules> {
  return apiFetch<Rules>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/scheduling-rule-override`,
    { method: 'PUT', body: rules, token },
  );
}

export async function getTeamAllowedLocationsOverride(
  token: string | null,
  slug: string,
  teamID: string,
): Promise<AllowedLocationsResponse> {
  return apiFetch<AllowedLocationsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/allowed-locations-override`,
    { token },
  );
}

export async function setTeamAllowedLocationsOverride(
  token: string | null,
  slug: string,
  teamID: string,
  entries: AllowedLocationEntry[],
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/allowed-locations-override`,
    { method: 'PUT', body: { entries }, token },
  );
}

export interface EffectiveRulesResponse {
  team_id: string;
  category_id: string;
  category_name: string;
  rules: Rules;
  provenance: Record<string, 'category' | 'override' | 'unset'>;
  locations: AllowedLocationEntry[];
  locations_from: 'category' | 'override' | 'unset';
}

export async function getEffectiveSchedulingRules(
  token: string | null,
  slug: string,
  teamID: string,
): Promise<EffectiveRulesResponse> {
  return apiFetch<EffectiveRulesResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/effective-scheduling-rules`,
    { token },
  );
}

export interface SchedulingRulesSummaryItem {
  team_id: string;
  has_rules: boolean;
}

export interface SchedulingRulesSummaryResponse {
  teams: SchedulingRulesSummaryItem[];
}

// Bulk per-team flag: does this team have any scheduling rule set (via
// category default or per-team override)? Used by the AI template
// generator to disable teams that would be filtered out anyway.
export async function getSchedulingRulesSummary(
  token: string | null,
  slug: string,
): Promise<SchedulingRulesSummaryResponse> {
  return apiFetch<SchedulingRulesSummaryResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/scheduling-rules-summary`,
    { token },
  );
}
